/**
 * MerchantContext — provides merchant locale settings (timezone, locale)
 * to the entire app tree without each page fetching independently.
 *
 * Sits inside AuthProvider (needs merchantId).
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getMerchantById, MerchantWithCurrency } from '../services/merchants.service';
import { getMerchantLocale } from '../lib/dateFormatting';

interface MerchantLocale {
  /** BCP 47 locale tag, e.g. "de-DE" */
  locale: string;
  /** IANA timezone, e.g. "Europe/Berlin" */
  timezone: string;
}

interface MerchantContextType {
  merchant: MerchantWithCurrency | null;
  merchantLocale: MerchantLocale;
  loading: boolean;
  /** Re-read the merchant row, e.g. after its settings were saved. */
  refreshMerchant: () => Promise<void>;
}

const DEFAULTS: MerchantLocale = { locale: 'en-US', timezone: 'UTC' };

const MerchantContext = createContext<MerchantContextType>({
  merchant: null,
  merchantLocale: DEFAULTS,
  loading: true,
  refreshMerchant: async () => {},
});

export function MerchantProvider({ children }: { children: React.ReactNode }) {
  const { merchantId } = useAuth();
  const [merchant, setMerchant] = useState<MerchantWithCurrency | null>(null);
  const [merchantLocale, setMerchantLocale] = useState<MerchantLocale>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const applyMerchant = (data: MerchantWithCurrency) => {
    setMerchant(data);
    setMerchantLocale({
      locale: getMerchantLocale(data.default_language, data.country_code),
      timezone: data.timezone || 'UTC',
    });
  };

  useEffect(() => {
    if (!merchantId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      const { data } = await getMerchantById(merchantId);
      if (cancelled) return;

      if (data) {
        applyMerchant(data);
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [merchantId]);

  const refreshMerchant = useCallback(async () => {
    if (!merchantId) return;
    const { data } = await getMerchantById(merchantId);
    if (data) applyMerchant(data);
  }, [merchantId]);

  return (
    <MerchantContext.Provider value={{ merchant, merchantLocale, loading, refreshMerchant }}>
      {children}
    </MerchantContext.Provider>
  );
}

export function useMerchant() {
  return useContext(MerchantContext);
}
